import dayjs from 'dayjs';

import { passwordMinLength } from '@/utils/password.config';
import type { Dayjs } from 'dayjs';
import Logger from '@/lib/logger';
import { ROLES } from '@/hooks/useNeedRole';

export const minAge = parseInt(String(import.meta.env.VITE_MINIMUM_AGE));

export function containsUppercase(ch: string) {
  return /[A-Z]/.test(ch);
}

export function containsSpecialChar(ch: string) {
  return /[`!@#$%^&*()_\-+=[\]{};':"\\|,.<>/?~ ]/.test(ch);
}

export function checkPassword(
  password: string
): [boolean, boolean, boolean, boolean] {
  let countOfUpperCase = 0,
    // countOfLowerCase = 0,
    countOfNumbers = 0,
    countOfSpecialChar = 0;
  const passwordLength = password.length;
  for (let i = 0; i < passwordLength; i++) {
    const ch = password.charAt(i);
    if (!isNaN(+ch)) countOfNumbers++;
    else if (containsUppercase(ch)) countOfUpperCase++;
    // else if (containsLowercase(ch)) countOfLowerCase++;
    else if (containsSpecialChar(ch)) countOfSpecialChar++;
  }

  return [
    passwordLength >= passwordMinLength,
    countOfUpperCase >= 1,
    countOfSpecialChar >= 1,
    countOfNumbers >= 1,
  ];
}

export function checkUsersAge(
  birthdate: null | string | Dayjs,
  minYears = minAge
) {
  if (birthdate === null) return false;

  const today = dayjs();
  const birthDateObj =
    typeof birthdate === 'string' ? dayjs(birthdate) : birthdate;

  if (!birthDateObj.isValid()) {
    Logger.error('Invalid birthdate provided:', birthdate);
    return false;
  }

  const usersAge = today.diff(birthDateObj, 'year');

  const effectiveMinAge = minYears >= 19 ? minYears - 1 : minYears;

  return usersAge >= effectiveMinAge;
}

export function generateSessionId() {
  const crypto =
    window.crypto ||
    // @ts-expect-error this is for compatibility
    window.msCrypto;

  if (!crypto || !crypto.getRandomValues) {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  }

  const array = new Uint32Array(8); // 8 * 4 = 32 bytes (256 bits)
  crypto.getRandomValues(array);
  return Array.from(array, (dec) => dec.toString(16).padStart(8, '0')).join('');
}

export function removeIdentifierFromObject(
  objStringified: string | undefined
): string | undefined {
  const fieldsToRemove = [
    'first_name',
    'last_name',
    'email',
    'address1',
    'address2',
  ];

  if (!objStringified) return objStringified;

  try {
    const sanitizedResponse = { ...JSON.parse(objStringified) };

    fieldsToRemove.forEach((field) => {
      delete sanitizedResponse[field];
    });

    return JSON.stringify(sanitizedResponse);
  } catch (error) {
    Logger.debug('Error sanitizing object:', error);
    return objStringified;
  }
}

export function getDefaultRedirectUri(
  hasSupportAccess: boolean,
  roles: string[]
): string {
  // if user only has report role, redirect to reporting page
  if (roles.length === 1 && roles.includes(ROLES.report)) {
    return '/support/admin/reporting';
  }

  return hasSupportAccess ? '/support' : '/member';
}
